import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Banknote, Ticket, Loader2, Gift, ArrowRight, Search, CreditCard, AlertTriangle, Copy, Send, Landmark, Wallet } from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { trpc } from '~/api/trpc';

const paymentMethodOptions = [
    { value: 'CASH', label: 'Bar', icon: Banknote },
    { value: 'VOUCHER', label: 'Gutschein', icon: Ticket },
    { value: 'KLARNA', label: 'Klarna (Später zahlen)', icon: CreditCard },
    { value: 'BANK_TRANSFER', label: 'Überweisung', icon: Landmark },
    { value: 'STRIPE_CHECKOUT', label: 'Stripe', icon: CreditCard },
    { value: 'BUNDLE_VOUCHER', label: 'Bundle-Gutschein', icon: Gift },
    { value: 'PAYPAL', label: 'PayPal', icon: Wallet },
    { value: 'SUMUP', label: 'SumUp', icon: CreditCard }
] as const;

const addPaymentSchema = z.object({
    amountEur: z.number().positive('Betrag muss positiv sein'),
    paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'STRIPE_CHECKOUT', 'SUMUP', 'PAYPAL', 'VOUCHER', 'BUNDLE_VOUCHER', 'KLARNA']),
    note: z.string().optional(),
});

type AddPaymentFormData = z.infer<typeof addPaymentSchema>;

type AddPaymentDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    billingDraftId: string;
    storeId: number;
    remainingAmountEur: number;
    onSuccess?: () => void;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

const AddPaymentDialog = ({
    open,
    onOpenChange,
    billingDraftId,
    storeId,
    remainingAmountEur,
    onSuccess,
}: AddPaymentDialogProps) => {
    const [selectedMethod, setSelectedMethod] = useState<string>('');
    const [selectedVoucherId, setSelectedVoucherId] = useState<string>('');
    const [selectedGiftCardId, setSelectedGiftCardId] = useState<string>('');
    const [selectedCashRegisterId, setSelectedCashRegisterId] = useState<string>('');
    const [selectedKlarnaAuthId, setSelectedKlarnaAuthId] = useState<string>('');
    const [giftCardSearch, setGiftCardSearch] = useState('');
    const [klarnaRequestAmountEur, setKlarnaRequestAmountEur] = useState(0);
    const [klarnaRequest, setKlarnaRequest] = useState<{ url: string; emailSentTo: string | null } | null>(null);
    const trpcUtils = trpc.useUtils();

    const form = useForm<AddPaymentFormData>({
        resolver: zodResolver(addPaymentSchema),
        defaultValues: {
            amountEur: 0,
            paymentMethod: 'CASH',
            note: '',
        },
    });

    // Track if payment method has been selected
    const isMethodSelected = selectedMethod !== '';
    const isBundleVoucher = selectedMethod === 'BUNDLE_VOUCHER';
    const isGiftCard = selectedMethod === 'VOUCHER';
    const isKlarna = selectedMethod === 'KLARNA';

    // Reset form state when dialog opens
    useEffect(() => {
        if (open) {
            setSelectedMethod('');
            setSelectedVoucherId('');
            setSelectedGiftCardId('');
            setSelectedCashRegisterId('');
            setSelectedKlarnaAuthId('');
            setGiftCardSearch('');
            setKlarnaRequestAmountEur(remainingAmountEur);
            setKlarnaRequest(null);
            form.reset({
                amountEur: 0,
                paymentMethod: 'CASH',
                note: '',
            });
        }
    }, [open, form, remainingAmountEur]);

    // Fetch cash registers if CASH is selected
    const { data: cashRegistersData } = trpc.cashRegisters.getByStore.useQuery(
        { storeId },
        { enabled: selectedMethod === 'CASH' && open }
    );

    // Auto-select default cash register when data loads and no register is selected
    useEffect(() => {
        if (cashRegistersData && !selectedCashRegisterId) {
            const defaultId = cashRegistersData.defaultCashRegisterId;
            const isDefaultActive = defaultId && cashRegistersData.cashRegisters.some((cr) => cr.id === defaultId);
            if (isDefaultActive) {
                setSelectedCashRegisterId(defaultId);
            }
        }
    }, [cashRegistersData, selectedCashRegisterId]);

    // Fetch applicable bundle vouchers when BUNDLE_VOUCHER is selected
    const { data: voucherData, isLoading: voucherLoading } = trpc.payments.getApplicableBundleVouchersForBillingDraft.useQuery(
        { billingDraftId },
        { enabled: isBundleVoucher && open }
    );

    // Fetch applicable gift cards when GIFT_CARD is selected
    const { data: giftCardData, isLoading: giftCardLoading } = trpc.payments.getApplicableGiftCardsForBillingDraft.useQuery(
        { billingDraftId, search: giftCardSearch || undefined },
        { enabled: isGiftCard && open }
    );

    // Klarna is only offered to customers with an active/expired authorization or an existing Klarna payment on this draft
    const { data: klarnaAuthData, isLoading: klarnaAuthLoading } = trpc.payments.getKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId },
        { enabled: open, refetchInterval: klarnaRequest ? 5_000 : false }
    );
    const hasKlarnaAuthorizations = (klarnaAuthData ?? []).length > 0;
    const { data: expiredKlarnaAuthData } = trpc.payments.getExpiredKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId },
        { enabled: open }
    );
    const { data: draftPayments } = trpc.payments.getByBillingDraft.useQuery(
        { billingDraftId },
        { enabled: open }
    );
    const hasKlarnaPaymentOnDraft = (draftPayments ?? []).some((p) => p.paymentMethod === 'KLARNA');
    const isKlarnaAvailable = hasKlarnaAuthorizations
        || (expiredKlarnaAuthData ?? []).length > 0
        || hasKlarnaPaymentOnDraft;
    const availablePaymentMethodOptions = paymentMethodOptions.filter(
        (option) => option.value !== 'KLARNA' || isKlarnaAvailable || selectedMethod === 'KLARNA'
    );
    const isKlarnaRequestMode = isKlarna && !klarnaAuthLoading && !hasKlarnaAuthorizations;

    const klarnaRequestMutation = trpc.payments.requestKlarnaAuthorizationForBillingDraft.useMutation({
        onSuccess: (data) => {
            setKlarnaRequest({ url: data.url, emailSentTo: data.emailSentTo });
            if (data.emailSentTo) {
                toast.success(`Link an ${data.emailSentTo} gesendet`);
            } else {
                toast.warning('Kunde hat keine E-Mail, bitte QR-Code nutzen');
            }
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });
    const isKlarnaRequestAmountInvalid = klarnaRequestAmountEur <= 0
        || Math.round(klarnaRequestAmountEur * 100) > Math.round(remainingAmountEur * 100);

    const enteredAmount = form.watch('amountEur') || 0;
    
    // Get the selected voucher
    const selectedVoucher = useMemo(() => {
        if (!voucherData || !selectedVoucherId) {
            return null;
        }
        return voucherData.vouchers.find(v => v.id === selectedVoucherId) ?? null;
    }, [voucherData, selectedVoucherId]);

    // Calculate matching line items and max payable for bundle voucher
    const bundleVoucherCalculation = useMemo(() => {
        if (!selectedVoucher || !voucherData) {
            return {
                matchingLineItems: [],
                totalOriginalPrice: 0,
                totalAdjustedPrice: 0,
                maxPayable: 0,
                totalUnitsNeeded: 0,
                unitsAvailable: 0,
                unitsThatCanBePaid: 0,
            };
        }

        // Filter line items matching the voucher's treatment variant
        const matchingLineItems = voucherData.lineItems
            .filter(li => li.treatmentVariantId === selectedVoucher.treatmentVariantId)
            .map(li => ({
                ...li,
                originalPrice: Number(li.totalGrossDiscounted),
                adjustedPrice: selectedVoucher.unitPriceGross,
            }));

        const totalOriginalPrice = matchingLineItems.reduce((sum, li) => sum + li.originalPrice, 0);
        const totalUnitsNeeded = matchingLineItems.length;
        const unitsAvailable = selectedVoucher.remainingUnits;

        let unitsThatCanBePaid = 0;
        let totalAdjustedPrice = 0;
        let remainingCharges = unitsAvailable;

        for (const lineItem of matchingLineItems) {
            if (remainingCharges >= 1) {
                unitsThatCanBePaid += 1;
                totalAdjustedPrice += lineItem.adjustedPrice;
                remainingCharges -= 1;
            }
        }

        const maxPayable = totalAdjustedPrice;

        return {
            matchingLineItems,
            totalOriginalPrice,
            totalAdjustedPrice,
            maxPayable,
            totalUnitsNeeded,
            unitsAvailable,
            unitsThatCanBePaid,
        };
    }, [selectedVoucher, voucherData]);

    const chargesToUse = useMemo(() => {
        if (!selectedVoucher || enteredAmount <= 0) {
            return 0;
        }

        let chargesUsed = 0;
        let remainingPayment = enteredAmount;

        for (const lineItem of bundleVoucherCalculation.matchingLineItems) {
            if (remainingPayment >= lineItem.adjustedPrice && 1 <= (selectedVoucher.remainingUnits - chargesUsed)) {
                chargesUsed += 1;
                remainingPayment -= lineItem.adjustedPrice;
            }
        }

        return chargesUsed;
    }, [selectedVoucher, enteredAmount, bundleVoucherCalculation.matchingLineItems]);

    // Get the selected gift card
    const selectedGiftCard = useMemo(() => {
        if (!giftCardData || !selectedGiftCardId) {
            return null;
        }
        return giftCardData.data.find((gc: any) => gc.id === selectedGiftCardId) ?? null;
    }, [giftCardData, selectedGiftCardId]);

    const giftCardMaxAmount = selectedGiftCard ? Math.min(selectedGiftCard.maxUsableAmount, remainingAmountEur) : 0;
    const isAmountExceedsGiftCard = isGiftCard && selectedGiftCard && Math.round(enteredAmount * 100) > Math.round(giftCardMaxAmount * 100);
    const hasNoApplicableGiftCards = isGiftCard && giftCardData && giftCardData.data.length === 0 && !giftCardSearch;

    // Validation: amount cannot exceed remaining balance
    const isAmountExceedsRemaining = !isBundleVoucher && !isGiftCard && Math.round(enteredAmount * 100) > Math.round(remainingAmountEur * 100);
    
    // Bundle voucher max validation
    const isAmountExceedsBundleMax = isBundleVoucher && Math.round(enteredAmount * 100) > Math.round(bundleVoucherCalculation.maxPayable * 100);
    
    // No applicable vouchers
    const hasNoApplicableVouchers = isBundleVoucher && voucherData && voucherData.vouchers.length === 0;

    // Standard payment mutation
    const addPaymentMutation = trpc.payments.addPaymentToBillingDraft.useMutation({
        onSuccess: () => {
            toast.success('Zahlung wurde hinzugefügt');
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraftId });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId });
            onOpenChange(false);
            form.reset();
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Bundle voucher payment mutation
    const bundleVoucherMutation = trpc.payments.applyBundleVoucherPayment.useMutation({
        onSuccess: (data) => {
            toast.success(`Bundle-Gutschein eingelöst: ${data.chargesConsumed} Einheiten verwendet`);
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraftId });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId });
            trpcUtils.payments.getApplicableBundleVouchersForBillingDraft.invalidate({ billingDraftId });
            onOpenChange(false);
            form.reset();
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Gift card payment mutation
    const giftCardMutation = trpc.payments.applyGiftCardPayment.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarte eingelöst');
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraftId });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId });
            trpcUtils.payments.getApplicableGiftCardsForBillingDraft.invalidate({ billingDraftId });
            onOpenChange(false);
            form.reset();
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Klarna capture mutation
    const klarnaMutation = trpc.payments.captureKlarnaPayment.useMutation({
        onSuccess: (data) => {
            toast.success(`Klarna-Zahlung eingezogen: ${formatCurrency(data.capturedAmountCents / 100)}`);
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraftId });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId });
            trpcUtils.payments.getKlarnaAuthorizationsForBillingDraft.invalidate({ billingDraftId });
            onOpenChange(false);
            form.reset();
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const isPending = addPaymentMutation.isPending || bundleVoucherMutation.isPending || giftCardMutation.isPending || klarnaMutation.isPending;

    const handleSubmit = form.handleSubmit((data) => {
        if (isKlarna) {
            if (!selectedKlarnaAuthId) {
                toast.error('Bitte wählen Sie eine Klarna-Autorisierung');
                return;
            }
            klarnaMutation.mutate({
                klarnaAuthorizationId: selectedKlarnaAuthId,
                billingDraftId,
            });
            return;
        }

        if (isBundleVoucher) {
            if (!selectedVoucherId) {
                toast.error('Bitte wählen Sie einen Bundle-Gutschein');
                return;
            }
            const paidAmountCents = Math.round(data.amountEur * 100);
            bundleVoucherMutation.mutate({
                billingDraftId,
                bundleVoucherId: selectedVoucherId,
                paymentAmountCents: paidAmountCents,
                note: data.note || undefined,
            });
        } else if (isGiftCard) {
            if (!selectedGiftCardId) {
                toast.error('Bitte wählen Sie eine Gutscheinkarte');
                return;
            }
            const paidAmountCents = Math.round(data.amountEur * 100);
            giftCardMutation.mutate({
                billingDraftId,
                giftCardId: selectedGiftCardId,
                paidAmountCents,
            });
        } else {
            const paidAmountCents = Math.round(data.amountEur * 100);
            addPaymentMutation.mutate({
                billingDraftId,
                paidAmountCents,
                paymentMethod: data.paymentMethod as Exclude<AddPaymentFormData['paymentMethod'], 'VOUCHER' | 'BUNDLE_VOUCHER' | 'KLARNA'>,
                note: data.note || undefined,
                ...(data.paymentMethod === 'CASH' && selectedCashRegisterId ? { cashRegisterId: selectedCashRegisterId } : {}),
            });
        }
    });

    const handleMethodChange = (value: string) => {
        setSelectedMethod(value);
        setSelectedVoucherId('');
        setSelectedGiftCardId('');
        setSelectedCashRegisterId('');
        setSelectedKlarnaAuthId('');
        setGiftCardSearch('');
        setKlarnaRequestAmountEur(remainingAmountEur);
        setKlarnaRequest(null);
        form.setValue('paymentMethod', value as AddPaymentFormData['paymentMethod']);
        // Reset amount to 0 when changing method - user must consciously enter amount
        form.setValue('amountEur', 0);
    };

    const handleVoucherSelect = (voucherId: string) => {
        setSelectedVoucherId(voucherId);
        // Reset amount when voucher changes
        form.setValue('amountEur', 0);
    };

    // Determine if submit should be disabled
    const isCash = selectedMethod === 'CASH';
    const isCashRegisterRequired = isCash && !selectedCashRegisterId;

    const isSubmitDisabled = () => {
        if (!isMethodSelected || isPending) {
            return true;
        }
        if (isKlarna) {
            return !selectedKlarnaAuthId;
        }
        if (enteredAmount <= 0) {
            return true;
        }
        if (isBundleVoucher) {
            return !selectedVoucherId || isAmountExceedsBundleMax || chargesToUse === 0;
        }
        if (isGiftCard) {
            return !selectedGiftCardId || !!isAmountExceedsGiftCard;
        }
        if (isCashRegisterRequired) {
            return true;
        }
        return isAmountExceedsRemaining;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[500px]'>
                <DialogHeader>
                    <DialogTitle>Zahlung hinzufügen</DialogTitle>
                    <DialogDescription>
                        Ausstehender Betrag: {formatCurrency(remainingAmountEur)}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        {/* Step 1: Payment Method Selection */}
                        <FormField
                            control={form.control}
                            name='paymentMethod'
                            render={() => (
                                <FormItem>
                                    <FormLabel>Zahlungsmethode</FormLabel>
                                    <Select
                                        value={selectedMethod}
                                        onValueChange={handleMethodChange}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder='Zahlungsmethode wählen' />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {availablePaymentMethodOptions.map((option) => {
                                                const Icon = option.icon;
                                                return (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        <div className='flex items-center gap-2'>
                                                            <Icon className='h-4 w-4' />
                                                            {option.label}
                                                        </div>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Step 2: Only show options after method is selected */}
                        {isMethodSelected && (
                            <>
                                {/* Cash register selection for CASH payments */}
                                {isCash && (
                                    <div className='space-y-2'>
                                        <FormLabel>Kasse</FormLabel>
                                        <Select
                                            value={selectedCashRegisterId}
                                            onValueChange={setSelectedCashRegisterId}
                                            disabled
                                        >
                                            <SelectTrigger className='opacity-70'>
                                                <SelectValue placeholder='Kasse auswählen' />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {cashRegistersData?.cashRegisters?.map((cr) => (
                                                    <SelectItem key={cr.id} value={cr.id}>
                                                        {cr.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {cashRegistersData && cashRegistersData.cashRegisters.length === 0 && (
                                            <Alert>
                                                <AlertDescription>
                                                    Keine Kassen für diesen Standort vorhanden. Bitte erstellen Sie zuerst eine Kasse.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </div>
                                )}

                                {/* Bundle Voucher Selection */}
                                {isBundleVoucher && (
                                    <div className='space-y-3'>
                                        {voucherLoading ? (
                                            <div className='flex items-center justify-center py-4'>
                                                <Loader2 className='h-5 w-5 animate-spin' />
                                            </div>
                                        ) : hasNoApplicableVouchers ? (
                                            <Alert>
                                                <AlertDescription>
                                                    Keine anwendbaren Bundle-Gutscheine gefunden. Gutscheine müssen der 
                                                    Treatment-Variante in den Entwurfspositionen entsprechen.
                                                </AlertDescription>
                                            </Alert>
                                        ) : (
                                            <>
                                                <FormLabel>Bundle-Gutschein auswählen</FormLabel>
                                                <div className='space-y-2 max-h-40 overflow-y-auto'>
                                                    {voucherData?.vouchers.map((voucher) => (
                                                        <div
                                                            key={voucher.id}
                                                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                                                selectedVoucherId === voucher.id
                                                                    ? 'border-primary bg-primary/5'
                                                                    : 'hover:bg-muted/50'
                                                            }`}
                                                            onClick={() => handleVoucherSelect(voucher.id)}
                                                        >
                                                            <div className='flex justify-between items-start'>
                                                                <div>
                                                                    <p className='font-medium text-sm'>{voucher.treatmentVariantName}</p>
                                                                    <p className='text-xs text-muted-foreground'>
                                                                        {formatCurrency(voucher.unitPriceGross)} pro Einheit
                                                                    </p>
                                                                </div>
                                                                <Badge variant='secondary' className='text-xs'>
                                                                    {voucher.remainingUnits} Einheiten
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Show matching line items and price adjustment preview */}
                                                {selectedVoucher && bundleVoucherCalculation.matchingLineItems.length > 0 && (
                                                    <>
                                                        <Separator />
                                                        <div className='space-y-2'>
                                                            <p className='text-sm font-medium'>Passende Entwurfspositionen:</p>
                                                            <div className='space-y-1 text-sm'>
                                                                {bundleVoucherCalculation.matchingLineItems.map((li) => (
                                                                    <div key={li.id} className='flex items-center gap-2 text-muted-foreground'>
                                                                        <span className='truncate flex-1'>
                                                                            Pos. {li.sequence}: {li.description}
                                                                        </span>
                                                                        <span className='line-through text-xs'>{formatCurrency(li.originalPrice)}</span>
                                                                        <ArrowRight className='h-3 w-3' />
                                                                        <span className='text-foreground font-medium'>{formatCurrency(li.adjustedPrice)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className='bg-muted/50 p-2 rounded text-xs space-y-1'>
                                                                <div className='flex justify-between'>
                                                                    <span>Benötigte Einheiten:</span>
                                                                    <span>{bundleVoucherCalculation.totalUnitsNeeded}</span>
                                                                </div>
                                                                <div className='flex justify-between'>
                                                                    <span>Verfügbare Einheiten:</span>
                                                                    <span>{bundleVoucherCalculation.unitsAvailable}</span>
                                                                </div>
                                                                <div className='flex justify-between font-medium'>
                                                                    <span>Maximaler Zahlungsbetrag:</span>
                                                                    <span>{formatCurrency(bundleVoucherCalculation.maxPayable)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {selectedVoucher && bundleVoucherCalculation.matchingLineItems.length === 0 && (
                                                    <Alert>
                                                        <AlertDescription>
                                                            Keine passende Entwurfsposition für diesen Gutschein gefunden.
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Gift Card Selection */}
                                {isGiftCard && (
                                    <div className='space-y-3'>
                                                <FormLabel>Gutscheinkarte suchen</FormLabel>
                                                <div className='relative'>
                                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                                    <Input
                                                        placeholder='Gutscheinkartennummer suchen...'
                                                        value={giftCardSearch}
                                                        onChange={(e) => setGiftCardSearch(e.target.value)}
                                                        className='pl-9'
                                                    />
                                                </div>

                                        {giftCardLoading ? (
                                            <div className='flex items-center justify-center py-4'>
                                                <Loader2 className='h-5 w-5 animate-spin' />
                                            </div>
                                        ) : (
                                            <>
                                                {hasNoApplicableGiftCards ? (
                                                    <Alert>
                                                        <AlertDescription>
                                                            Keine anwendbaren Gutscheinkarten gefunden.
                                                        </AlertDescription>
                                                    </Alert>
                                                ) : (
                                                    <div className='space-y-2 max-h-40 overflow-y-auto'>
                                                        {giftCardData?.data.map((gc: any) => (
                                                            <div
                                                                key={gc.id}
                                                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                                                    selectedGiftCardId === gc.id
                                                                        ? 'border-primary bg-primary/5'
                                                                        : 'hover:bg-muted/50'
                                                                }`}
                                                                onClick={() => {
                                                                    setSelectedGiftCardId(gc.id);
                                                                    form.setValue('amountEur', Math.min(gc.maxUsableAmount, remainingAmountEur));
                                                                }}
                                                            >
                                                                <div className='flex justify-between items-start'>
                                                                    <div>
                                                                        <p className='font-medium text-sm'>{gc.giftCardNumber}</p>
                                                                        <p className='text-xs text-muted-foreground'>{gc.name}</p>
                                                                        {gc.isSuggested && (
                                                                            <Badge variant='secondary' className='mt-1 text-[10px]'>Vorgeschlagen</Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className='text-right'>
                                                                        <Badge variant='outline' className='text-xs'>
                                                                            {formatCurrency(gc.remainingBalance)}
                                                                        </Badge>
                                                                        <p className='text-[10px] text-muted-foreground mt-1'>
                                                                            Max: {formatCurrency(gc.maxUsableAmount)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {selectedGiftCard && (
                                                    <>
                                                        <Separator />
                                                        <div className='bg-muted/50 p-2 rounded text-xs space-y-1'>
                                                            <div className='flex justify-between'>
                                                                <span>Restguthaben:</span>
                                                                <span>{formatCurrency(selectedGiftCard.remainingBalance)}</span>
                                                            </div>
                                                            <div className='flex justify-between'>
                                                                <span>Typ:</span>
                                                                <span>{selectedGiftCard.type === 'USE_EVERYWHERE' ? 'Überall einsetzbar' : 'Eingeschränkt'}</span>
                                                            </div>
                                                            <div className='flex justify-between font-medium'>
                                                                <span>Max. Zahlungsbetrag:</span>
                                                                <span>{formatCurrency(giftCardMaxAmount)}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Klarna Authorization Selection */}
                                {isKlarna && (
                                    <div className='space-y-3'>
                                        {klarnaAuthLoading ? (
                                            <div className='flex items-center justify-center py-4'>
                                                <Loader2 className='h-5 w-5 animate-spin' />
                                            </div>
                                        ) : !klarnaAuthData || klarnaAuthData.length === 0 ? (
                                            <div className='space-y-3'>
                                                <Alert>
                                                    <AlertDescription>
                                                        Keine gültige Klarna-Vorautorisierung vorhanden. Sie können dem Kunden
                                                        einen Link senden, um eine neue Autorisierung zu erteilen.
                                                    </AlertDescription>
                                                </Alert>

                                                {!klarnaRequest ? (
                                                    <>
                                                        <div className='space-y-2'>
                                                            <FormLabel>Betrag der Autorisierung (EUR)</FormLabel>
                                                            <Input
                                                                type='number'
                                                                step='0.01'
                                                                min='0.01'
                                                                max={remainingAmountEur}
                                                                value={klarnaRequestAmountEur || ''}
                                                                onChange={(e) => setKlarnaRequestAmountEur(parseFloat(e.target.value) || 0)}
                                                            />
                                                            <div className='text-xs text-muted-foreground'>
                                                                Max: {formatCurrency(remainingAmountEur)}
                                                            </div>
                                                            {isKlarnaRequestAmountInvalid && (
                                                                <div className='text-xs text-destructive'>
                                                                    Betrag muss zwischen 0,01 € und {formatCurrency(remainingAmountEur)} liegen
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button
                                                            type='button'
                                                            className='w-full'
                                                            disabled={isKlarnaRequestAmountInvalid || klarnaRequestMutation.isPending}
                                                            onClick={() => klarnaRequestMutation.mutate({
                                                                billingDraftId,
                                                                amountCents: Math.round(klarnaRequestAmountEur * 100),
                                                            })}
                                                        >
                                                            {klarnaRequestMutation.isPending ? (
                                                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                                            ) : (
                                                                <Send className='mr-2 h-4 w-4' />
                                                            )}
                                                            An Kunden senden
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <div className='border rounded-lg p-4 space-y-3'>
                                                        <p className='text-sm'>
                                                            {klarnaRequest.emailSentTo
                                                                ? <>Link wurde an <span className='font-medium'>{klarnaRequest.emailSentTo}</span> gesendet. Alternativ kann der Kunde den QR-Code scannen.</>
                                                                : 'Kunde hat keine E-Mail-Adresse. Bitte den QR-Code scannen lassen.'}
                                                        </p>
                                                        <div className='flex justify-center bg-white p-3 rounded'>
                                                            <QRCode value={klarnaRequest.url} size={160} />
                                                        </div>
                                                        <Button
                                                            type='button'
                                                            variant='outline'
                                                            className='w-full'
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(klarnaRequest.url);
                                                                toast.success('Link kopiert');
                                                            }}
                                                        >
                                                            <Copy className='mr-2 h-4 w-4' />
                                                            Link kopieren
                                                        </Button>
                                                        <div className='flex items-center justify-center gap-2 text-xs text-muted-foreground'>
                                                            <Loader2 className='h-3 w-3 animate-spin' />
                                                            Warte auf Autorisierung durch den Kunden (Link 24 Stunden gültig)
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <FormLabel>Klarna-Vorautorisierung auswählen</FormLabel>
                                                <div className='space-y-2 max-h-48 overflow-y-auto'>
                                                    {klarnaAuthData.map((auth) => {
                                                        const expiresAt = auth.expiresAt ? new Date(auth.expiresAt) : null;
                                                        const isExpiringSoon = expiresAt && expiresAt.getTime() - Date.now() < 3 * 24 * 60 * 60 * 1000;

                                                        return (
                                                            <div
                                                                key={auth.id}
                                                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                                                    selectedKlarnaAuthId === auth.id
                                                                        ? 'border-primary bg-primary/5'
                                                                        : 'hover:bg-muted/50'
                                                                }`}
                                                                onClick={() => {
                                                                    setSelectedKlarnaAuthId(auth.id);
                                                                    form.setValue('amountEur', auth.amountCents / 100);
                                                                }}
                                                            >
                                                                <div className='flex justify-between items-start'>
                                                                    <div className='space-y-1'>
                                                                        <div className='flex items-center gap-2'>
                                                                            <p className='font-medium text-sm'>
                                                                                {formatCurrency(auth.amountCents / 100)}
                                                                            </p>
                                                                            {auth.isLinkedToThisBillingDraft && (
                                                                                <Badge variant='secondary' className='text-[10px]'>
                                                                                    Zugehörig
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        <p className='text-xs text-muted-foreground'>
                                                                            Vorautorisiert am {format(new Date(auth.createdAt), 'dd.MM.yy HH:mm', { locale: de })}
                                                                        </p>
                                                                        {auth.appointment && (
                                                                            <p className='text-xs text-muted-foreground'>
                                                                                Termin: {auth.appointment.titleShort || auth.appointment.title}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <div className='text-right'>
                                                                        {isExpiringSoon && (
                                                                            <div className='flex items-center gap-1 text-amber-600 text-xs mb-1'>
                                                                                <AlertTriangle className='h-3 w-3' />
                                                                                <span>Bald ablaufend</span>
                                                                            </div>
                                                                        )}
                                                                        {expiresAt && (
                                                                            <p className='text-[10px] text-muted-foreground'>
                                                                                Läuft ab {formatDistanceToNow(expiresAt, { addSuffix: true, locale: de })}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {selectedKlarnaAuthId && (
                                                    <>
                                                        <Separator />
                                                        <div className='bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 rounded-lg text-sm space-y-1'>
                                                            <p className='font-medium text-blue-800 dark:text-blue-200'>Klarna Pay Later</p>
                                                            <p className='text-xs text-blue-700 dark:text-blue-300'>
                                                                Dieser Betrag wurde vom Kunden bei der Buchung vorautorisiert.
                                                                Beim Einziehen wird der Betrag über Klarna abgebucht. Der eingezogene
                                                                Betrag kann gleich oder niedriger als der autorisierte Betrag sein.
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Amount input - show for all methods, but with different max for bundle voucher / gift card */}
                                {!isKlarnaRequestMode && (!isBundleVoucher && !isGiftCard || (isBundleVoucher && selectedVoucherId && bundleVoucherCalculation.maxPayable > 0) || (isGiftCard && selectedGiftCardId && giftCardMaxAmount > 0)) && (
                                    <FormField
                                        control={form.control}
                                        name='amountEur'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Betrag (EUR)</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type='number'
                                                        step='0.01'
                                                        min='0.01'
                                                        max={isBundleVoucher ? bundleVoucherCalculation.maxPayable : isGiftCard ? giftCardMaxAmount : remainingAmountEur}
                                                        placeholder='0.00'
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                                    />
                                                </FormControl>
                                                <div className='text-xs text-muted-foreground'>
                                                    Max: {formatCurrency(isBundleVoucher ? bundleVoucherCalculation.maxPayable : isGiftCard ? giftCardMaxAmount : remainingAmountEur)}
                                                </div>
                                                {isAmountExceedsRemaining && (
                                                    <div className='text-xs text-destructive'>
                                                        Betrag darf nicht höher als {formatCurrency(remainingAmountEur)} sein
                                                    </div>
                                                )}
                                                {isAmountExceedsBundleMax && (
                                                    <div className='text-xs text-destructive'>
                                                        Betrag darf nicht höher als {formatCurrency(bundleVoucherCalculation.maxPayable)} sein
                                                    </div>
                                                )}
                                                {isAmountExceedsGiftCard && (
                                                    <div className='text-xs text-destructive'>
                                                        Betrag darf nicht höher als {formatCurrency(giftCardMaxAmount)} sein
                                                    </div>
                                                )}
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {/* Show charges to use for bundle voucher */}
                                {isBundleVoucher && selectedVoucherId && enteredAmount > 0 && chargesToUse > 0 && (
                                    <div className='bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3 rounded-lg text-sm'>
                                        <div className='flex justify-between items-center'>
                                            <span>Einheiten, die verwendet werden:</span>
                                            <span className='font-bold'>{chargesToUse}</span>
                                        </div>
                                        <div className='text-xs text-muted-foreground mt-1'>
                                            Verbleibend nach Zahlung: {selectedVoucher!.remainingUnits - chargesToUse} Einheiten
                                        </div>
                                    </div>
                                )}

                                {/* Note input */}
                                {!isKlarnaRequestMode && (
                                    <FormField
                                        control={form.control}
                                        name='note'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Notiz (optional)</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder='Zusätzliche Informationen zur Zahlung...'
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </>
                        )}

                        <DialogFooter>
                            <Button
                                type='button'
                                variant='outline'
                                onClick={() => onOpenChange(false)}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                type='submit'
                                disabled={isSubmitDisabled()}
                            >
                                {isPending && (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                )}
                                {isKlarna ? 'Klarna einziehen' : isBundleVoucher ? 'Bundle einlösen' : isGiftCard ? 'Gutschein einlösen' : 'Zahlung hinzufügen'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default AddPaymentDialog;
